import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth.jsx';
import { useTheme } from '../hooks/useTheme';


import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import Swal from 'sweetalert2';
import Pagination from '../components/common/Pagination';
import ComprobanteViewer from '../components/pagos/ComprobanteViewer';
import ModalEditarPago from '../components/pagos/ModalEditarPago';

const ComprobantesList = () => {
    // Style for the premium switch (matching Caja Chica)
    const switchStyle = `
        .dark-mode .form-switch .form-check-input {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba%28255, 255, 255, 0.45%29'/%3e%3c/svg%3e") !important;
            background-repeat: no-repeat !important;
            background-position: left center !important;
        }
        .dark-mode .form-switch .form-check-input:checked {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e") !important;
            background-position: right center !important;
            background-color: #0d6efd !important;
            border-color: #0d6efd !important;
        }
        .form-check-input {
            cursor: pointer;
            width: 3em !important;
            height: 1.5em !important;
        }
        .table-primary-light {
            background-color: rgba(13, 110, 253, 0.05) !important;
        }
        .dark-mode .table-primary-light {
            background-color: rgba(13, 110, 253, 0.15) !important;
        }
    `;

    const { theme } = useTheme();
    const { user } = useAuth();
    const [comprobantes, setComprobantes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Helpers for persistence
    const getStored = (key, defaultValue) => {
        const saved = localStorage.getItem(`comprobantes_${key}`);
        if (saved === null) return defaultValue;
        try {
            return JSON.parse(saved);
        } catch {
            return saved;
        }
    };

    // Filters
    const [startDate, setStartDate] = useState(() => {
        const saved = localStorage.getItem('comprobantes_startDate');
        return saved ? dayjs(saved) : null;
    });
    const [endDate, setEndDate] = useState(() => {
        const saved = localStorage.getItem('comprobantes_endDate');
        return saved ? dayjs(saved) : null;
    });
    const [searchTerm, setSearchTerm] = useState(() => getStored('searchTerm', ''));
    const [numeroFilter, setNumeroFilter] = useState(() => getStored('numeroFilter', ''));
    const [showOnlyMine, setShowOnlyMine] = useState(() => getStored('showOnlyMine', false));
    const [metodoFilter, setMetodoFilter] = useState(() => getStored('metodoFilter', ''));
    const [conceptoFilter, setConceptoFilter] = useState(() => getStored('conceptoFilter', ''));



    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // MUI Theme for DatePicker
    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: theme,
        },
    }), [theme]);

    // Modals
    const [showViewer, setShowViewer] = useState(false);
    const [selectedComprobante, setSelectedComprobante] = useState(null);
    const [showEditPagoModal, setShowEditPagoModal] = useState(false);
    const [pagoToEdit, setPagoToEdit] = useState(null);

    useEffect(() => {
        fetchComprobantes();
        // Persist filters
        localStorage.setItem('comprobantes_startDate', startDate ? startDate.toISOString() : '');
        localStorage.setItem('comprobantes_endDate', endDate ? endDate.toISOString() : '');
        localStorage.setItem('comprobantes_searchTerm', JSON.stringify(searchTerm));
        localStorage.setItem('comprobantes_numeroFilter', JSON.stringify(numeroFilter));
        localStorage.setItem('comprobantes_showOnlyMine', JSON.stringify(showOnlyMine));
        localStorage.setItem('comprobantes_metodoFilter', JSON.stringify(metodoFilter));
        localStorage.setItem('comprobantes_conceptoFilter', JSON.stringify(conceptoFilter));
    }, [showOnlyMine, metodoFilter, conceptoFilter, startDate, endDate, searchTerm, numeroFilter]);



    const fetchComprobantes = async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.startDate = startDate.format('YYYY-MM-DD');
            if (endDate) params.endDate = endDate.format('YYYY-MM-DD');
            if (searchTerm) params.search = searchTerm;
            if (numeroFilter) params.numero = numeroFilter;
            if (showOnlyMine && user) params.emitido_por = user.id;
            if (metodoFilter) params.metodo = metodoFilter;
            if (conceptoFilter) params.concepto = conceptoFilter;

            const response = await axios.get('/api/comprobantes', { params });
            setComprobantes(response.data);

        } catch (error) {
            console.error('Error fetching comprobantes:', error);
            Swal.fire('Error', 'No se pudieron cargar los comprobantes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1); // Reset to first page on new search
        fetchComprobantes();
    };

    const handleClearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSearchTerm('');
        setNumeroFilter('');
        setShowOnlyMine(false);
        setMetodoFilter('');
        setConceptoFilter('');
        setCurrentPage(1); // Reset to first page
    };

    const handleReenviar = async (comprobante) => {
        try {
            Swal.fire({
                title: 'Enviando a SUNAT...',
                text: 'Por favor espere mientras nos comunicamos con SUNAT.',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });
            const response = await axios.post(`/api/comprobantes/${comprobante.id}/reenviar`);
            if (response.data.success) {
                Swal.fire('¡Éxito!', 'Comprobante aceptado por SUNAT', 'success');
            } else {
                Swal.fire('Error SUNAT', response.data.message || 'El comprobante fue rechazado', 'error');
            }
            fetchComprobantes();
        } catch (error) {
            console.error(error);
            Swal.fire('Error', error.response?.data?.error || 'No se pudo conectar con SUNAT', 'error');
        }
    };

    const handleResetSunat = async (comprobante) => {
        const result = await Swal.fire({
            title: '¿Restablecer estado?',
            text: 'Esto cambiará el estado a Error/Pendiente para que puedas enviarlo al servidor oficial de Producción.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, restablecer',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await axios.post(`/api/comprobantes/${comprobante.id}/reset-sunat`);
                Swal.fire('Restablecido', 'El comprobante ya está listo para ser reenviado.', 'success');
                fetchComprobantes();
            } catch (error) {
                console.error(error);
                Swal.fire('Error', error.response?.data?.error || 'No se pudo restablecer', 'error');
            }
        }
    };


    const handleDelete = async (comprobante) => {
        const isChica = comprobante.isChica;
        const result = await Swal.fire({
            title: isChica ? '¿Anular Recibo de Caja?' : '¿Anular Comprobante?',
            text: isChica
                ? 'El movimiento de caja chica será anulado y el saldo se restaurará.'
                : `Será eliminado permanentemente. Si es el último emitido, la serie se restaurará. La deuda volverá al cronograma.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, anular',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                // Show loading
                Swal.fire({
                    title: 'Procesando...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                if (isChica) {
                    // Use chica_movimiento_id if it's a hybrid official record, otherwise use the direct id
                    const idToDelete = comprobante.chica_movimiento_id || comprobante.id;
                    await axios.post(`/api/caja/chica/anular/${idToDelete}`);
                } else {
                    await axios.delete(`/api/comprobantes/${comprobante.id}`);
                }

                Swal.fire(
                    '¡Anulado!',
                    isChica ? 'El recibo de caja ha sido anulado.' : 'El comprobante ha sido elimnado y la deuda restaurada.',
                    'success'
                );
                fetchComprobantes();
            } catch (error) {
                console.error('Error deleting comprobante:', error);
                Swal.fire(
                    'Error',
                    'No se pudo eliminar el registro. ' + (error.response?.data?.error || ''),
                    'error'
                );
            }
        }
    };

    const handlePrint = (comprobante) => {
        // Only use thermal print if it's a pure Caja Chica receipt (RI-)
        if (comprobante.isChica && comprobante.tipo === 'RECIBO') {
            const idToPrint = comprobante.chica_movimiento_id || comprobante.id;
            const url = `${axios.defaults.baseURL || ''}/api/caja/chica/recibo/${idToPrint}/imprimir`;
            window.open(url, '_blank', 'width=400,height=600');
            return;
        }

        setSelectedComprobante({
            comprobante_id: comprobante.id,
            numero_completo: `${comprobante.serie}-${comprobante.numero}`
        });
        setShowViewer(true);
    };

    const handleEditPago = (comprobante) => {
        setPagoToEdit({
            comprobante_id: comprobante.id,
            ...(comprobante.Pago || {}),
            cliente_nombre: comprobante.cliente_nombre,
            cliente_documento: comprobante.cliente_documento,
            isChica: comprobante.isChica
        });
        setShowEditPagoModal(true);
    };

    const getDisplayName = (user) => {
        if (!user) return '-';

        let fullName = user.username;
        if (user.personal?.apellidos_nombres) fullName = user.personal.apellidos_nombres;
        else if (user.alumno?.apellidos_nombres) fullName = user.alumno.apellidos_nombres;

        if (!fullName) return user.username || '-';
        if (!fullName.includes(' ')) return fullName;

        // Assume "Apellidos Nombres" -> Take last word as Name
        const parts = fullName.trim().replaceAll(',', '').split(/\s+/);
        const name = parts[parts.length - 1]; // Last word usually first name in "Apellidos Nombres" format
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    // Pagination calculations
    const totalPages = Math.ceil(comprobantes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentComprobantes = comprobantes.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Filter visibility
    const [showFilters, setShowFilters] = useState(() => {
        const saved = localStorage.getItem('comprobantes_showFilters');
        return saved !== null ? JSON.parse(saved) : true;
    });

    useEffect(() => {
        localStorage.setItem('comprobantes_showFilters', JSON.stringify(showFilters));
    }, [showFilters]);

    return (
        <div className={`comprobantes-list-page ${theme === 'dark' ? 'dark-mode' : ''}`}>
            <style>{switchStyle}</style>


            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="#">Caja</a></li>
                    <li className="breadcrumb-item active"><a href="#">Gestión de Comprobantes</a></li>
                </ol>
            </div>

            <Row>
                <Col md={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h4 className="card-title">Comprobantes Emitidos</h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant={showFilters ? "primary" : "outline-primary"}
                                    size="sm"
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="rounded-pill"
                                >
                                    <i className={`fa fa-sliders me-2`}></i>
                                    {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3 d-flex flex-wrap gap-2 animate__animated animate__fadeIn">

                                {startDate && (
                                    <Badge bg="info" className="rounded-pill p-2 px-3" style={{ cursor: 'pointer' }} onClick={() => setStartDate(null)}>
                                        Desde: {startDate.format('DD/MM/YYYY')} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {endDate && (
                                    <Badge bg="info" className="rounded-pill p-2 px-3" style={{ cursor: 'pointer' }} onClick={() => setEndDate(null)}>
                                        Hasta: {endDate.format('DD/MM/YYYY')} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {searchTerm && (
                                    <Badge bg="primary" className="rounded-pill p-2 px-3" style={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')}>
                                        Cliente: {searchTerm} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {numeroFilter && (
                                    <Badge bg="secondary" className="rounded-pill p-2 px-3" style={{ cursor: 'pointer' }} onClick={() => setNumeroFilter('')}>
                                        Nº: {numeroFilter} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {showOnlyMine && (
                                    <Badge bg="dark" className="rounded-pill p-2 px-3 text-white" style={{ cursor: 'pointer' }} onClick={() => setShowOnlyMine(false)}>
                                        Emitidos por mí <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {metodoFilter && (
                                    <Badge bg="warning" className="rounded-pill p-2 px-3 text-dark" style={{ cursor: 'pointer' }} onClick={() => setMetodoFilter('')}>
                                        Método: {metodoFilter} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                                {conceptoFilter && (
                                    <Badge bg="info" className="rounded-pill p-2 px-3 text-dark" style={{ cursor: 'pointer', backgroundColor: '#e0f2fe' }} onClick={() => setConceptoFilter('')}>
                                        Concepto: {conceptoFilter} <i className="fa fa-times ms-2"></i>
                                    </Badge>
                                )}
                            </div>


                            {showFilters && (
                                <Form onSubmit={handleSearch} className="animate__animated animate__fadeIn mb-4 p-3 border rounded shadow-sm">
                                    <ThemeProvider theme={muiTheme}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                            <Row className="align-items-end">
                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label className="small mb-1">Desde</Form.Label>
                                                        <DatePicker
                                                            value={startDate}
                                                            onChange={(newValue) => setStartDate(newValue)}
                                                            format="DD/MM/YYYY"
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    fullWidth: true,
                                                                    placeholder: 'DD/MM/YYYY',
                                                                    sx: {
                                                                        '& .MuiInputBase-root': {
                                                                            backgroundColor: theme === 'dark' ? '#2b3035' : '#fff',
                                                                            color: theme === 'dark' ? '#fff' : '#212529',
                                                                            height: '38px',
                                                                            fontSize: '0.875rem',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px 12px',
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            color: theme === 'dark' ? '#adb5bd' : '#6c757d',
                                                                            fontSize: '1.2rem',
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label className="small mb-1">Hasta</Form.Label>
                                                        <DatePicker
                                                            value={endDate}
                                                            onChange={(newValue) => setEndDate(newValue)}
                                                            format="DD/MM/YYYY"
                                                            slotProps={{
                                                                textField: {
                                                                    size: 'small',
                                                                    fullWidth: true,
                                                                    placeholder: 'DD/MM/YYYY',
                                                                    sx: {
                                                                        '& .MuiInputBase-root': {
                                                                            backgroundColor: theme === 'dark' ? '#2b3035' : '#fff',
                                                                            color: theme === 'dark' ? '#fff' : '#212529',
                                                                            height: '38px',
                                                                            fontSize: '0.875rem',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            padding: '8px 12px',
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            color: theme === 'dark' ? '#adb5bd' : '#6c757d',
                                                                            fontSize: '1.2rem',
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Group>
                                                        <Form.Label>Cliente (Nombre/DNI)</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Buscar..."
                                                            value={searchTerm}
                                                            onChange={e => {
                                                                setSearchTerm(e.target.value);
                                                                setCurrentPage(1);
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label>Número</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Ej: 5964"
                                                            value={numeroFilter}
                                                            onChange={e => {
                                                                setNumeroFilter(e.target.value);
                                                                setCurrentPage(1);
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label>Método Pago</Form.Label>
                                                        <Form.Select
                                                            value={metodoFilter}
                                                            onChange={e => {
                                                                setMetodoFilter(e.target.value);
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            <option value="">Todos</option>
                                                            <option value="EFECTIVO">Efectivo</option>
                                                            <option value="YAPE">Yape</option>
                                                            <option value="PLIN">Plin</option>
                                                            <option value="TRANSFERENCIA">Transferencia</option>
                                                            <option value="TARJETA">Tarjeta</option>
                                                            <option value="DEPOSITO">Depósito</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Group>
                                                        <Form.Label>Concepto</Form.Label>
                                                        <Form.Select
                                                            value={conceptoFilter}
                                                            onChange={e => setConceptoFilter(e.target.value)}
                                                        >
                                                            <option value="">Todos los conceptos</option>
                                                            <option value="MATRICULA">Matrícula</option>
                                                            <option value="PENSION">Pensión</option>
                                                            <option value="CUOTA_INGRESO">Cuota de Ingreso</option>
                                                            <option value="CUOTA_ANUAL">Cuota Anual</option>
                                                            <option value="AGENDA">Agenda</option>
                                                            <option value="MATERIAL_EDUCATIVO">Material Educativo</option>
                                                            <option value="UNIFORME">Uniforme</option>
                                                            <option value="OTROS">Otros</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2} className="d-flex align-items-center">
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="mine-switch"
                                                            checked={showOnlyMine}
                                                            onChange={(e) => setShowOnlyMine(e.target.checked)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <label className="form-check-label ms-2 text-muted fw-bold" htmlFor="mine-switch" style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.85rem' }}>
                                                            Ver mios
                                                        </label>
                                                    </div>
                                                </Col>


                                                <Col md={1}>
                                                    <Form.Group>
                                                        <Form.Label>Ver</Form.Label>
                                                        <Form.Select
                                                            value={itemsPerPage}
                                                            onChange={(e) => {
                                                                setItemsPerPage(Number(e.target.value));
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            <option value={5}>5</option>
                                                            <option value={10}>10</option>
                                                            <option value={20}>20</option>
                                                            <option value={50}>50</option>
                                                            <option value={100}>100</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={2} className="d-flex gap-2 ms-auto">
                                                    <Button variant="primary" type="submit" size="sm" className="w-100">
                                                        <i className="flaticon-381-search-2 me-2"></i> Buscar
                                                    </Button>
                                                    <Button variant="outline-secondary" size="sm" onClick={handleClearFilters} title="Limpiar">
                                                        <i className="fa fa-eraser"></i>
                                                    </Button>
                                                </Col>

                                            </Row>
                                        </LocalizationProvider>
                                    </ThemeProvider>
                                </Form>
                            )}

                            <Table responsive hover variant={theme === 'dark' ? 'dark' : ''}>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Tipo</th>
                                        <th>Número</th>
                                        <th>Cliente</th>
                                        <th>Total</th>
                                        <th>Método</th>
                                        <th>Emisor</th>
                                        <th>Estado</th>
                                        <th className="text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="8" className="text-center">Cargando...</td></tr>
                                    ) : currentComprobantes.length === 0 ? (
                                        <tr><td colSpan="8" className="text-center">No se encontraron comprobantes</td></tr>
                                    ) : (
                                        currentComprobantes.map(c => (
                                            <tr key={c.isChica ? `chica-${c.id}` : c.id} className={c.isChica ? 'table-primary-light' : ''}>
                                                <td>
                                                    {(() => {
                                                        if (!c.fecha_emision) return '-';
                                                        // Standardize parsing - handle strings and dates correctly
                                                        const d = new Date(c.fecha_emision);
                                                        if (isNaN(d.getTime())) return '-';

                                                        return d.toLocaleString('es-PE', {
                                                            timeZone: 'America/Lima',
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit',
                                                            hour12: true
                                                        });
                                                    })()}
                                                </td>
                                                <td>
                                                    <Badge bg={c.isChica ? "primary" : "info"}>
                                                        {c.tipo} {c.isChica && <small className="d-block" style={{ fontSize: '0.65rem' }}>CAJA {c.modulo}</small>}
                                                    </Badge>
                                                </td>
                                                <td className="fw-bold">{c.serie}-{String(c.numero).padStart(c.isChica ? 6 : 8, '0')}</td>
                                                <td>
                                                    <div>{c.cliente_nombre}</div>
                                                    <small className="text-muted">{c.cliente_documento_tipo}: {c.cliente_documento}</small>
                                                </td>
                                                <td className="fw-bold text-success">S/ {parseFloat(c.total).toFixed(2)}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Badge bg="light" text="dark" className="border">
                                                            {c.Pago?.metodo || 'N/A'}
                                                        </Badge>
                                                        {c.Pago?.referencia && (
                                                            <small className="ms-2 text-muted" title={c.Pago.referencia}>
                                                                #{c.Pago.referencia}
                                                            </small>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{getDisplayName(c.emisor)}</td>
                                                <td>
                                                    {c.estado === 'EMITIDO' ? (
                                                        <Badge bg="success">EMITIDO</Badge>
                                                    ) : (
                                                        <Badge bg="danger">{c.estado}</Badge>
                                                    )}
                                                    {!c.isChica && (
                                                        <div className="mt-1">
                                                            {c.sunat_estado === 'ACEPTADO' ? (
                                                                <Badge bg="success"><i className="fa fa-check me-1"></i>SUNAT OK</Badge>
                                                            ) : c.sunat_estado === 'ERROR' ? (
                                                                <Badge bg="danger" title={c.sunat_mensaje} style={{cursor: 'help'}}><i className="fa fa-times me-1"></i>SUNAT ERROR</Badge>
                                                            ) : (
                                                                <Badge bg="warning" text="dark"><i className="fa fa-clock-o me-1"></i>PENDIENTE</Badge>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    {!c.isChica && c.sunat_estado === 'ERROR' && c.estado === 'EMITIDO' && (
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleReenviar(c)}
                                                            title="Reenviar a SUNAT"
                                                        >
                                                            <i className="fa fa-paper-plane"></i>
                                                        </Button>
                                                    )}
                                                    {!c.isChica && c.sunat_estado === 'ACEPTADO' && c.estado === 'EMITIDO' && (
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleResetSunat(c)}
                                                            title="Restablecer para enviar a Producción"
                                                        >
                                                            <i className="fa fa-undo"></i>
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={() => handleEditPago(c)}
                                                        title="Editar Pago"
                                                    >
                                                        <i className="fa fa-pencil"></i>
                                                    </Button>
                                                    <Button
                                                        variant="info"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={() => handlePrint(c)}
                                                        title="Imprimir"
                                                    >
                                                        <i className="fa fa-print"></i>
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(c)}
                                                        title="Eliminar / Anular"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>

                            {/* Pagination and record count */}
                            {!loading && comprobantes.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Mostrando {startIndex + 1} a {Math.min(endIndex, comprobantes.length)} de {comprobantes.length} registros
                                    </div>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <ComprobanteViewer
                show={showViewer}
                onHide={() => setShowViewer(false)}
                comprobanteData={selectedComprobante}
            />

            <ModalEditarPago
                show={showEditPagoModal}
                onHide={() => setShowEditPagoModal(false)}
                pagoData={pagoToEdit}
                onSuccess={() => fetchComprobantes()}
            />
        </div>
    );
};


export default ComprobantesList;
